import { useSelector } from "react-redux";
import clsx from "clsx";
import DetectRTC from "detectrtc";
import PropTypes from "prop-types";
import { isNull } from "lodash";
import { getExactBrowserName, isOfflinePhotoCaptureFeatureAvailable } from "../../shared/utility";
import { CALL_DATA, GLOBAL_MEETING_DATA } from "../../constants/PublicVariables";
import { MEDIA_PERMISSIONS } from "../../constants/CallSettingConstants";
import { DEVICE_TYPES, NQ_UNSTABLE_PARAM } from "../../constants/Constants";
import ActiveSpeaker from "../Twilio/DominantSpeakerAnalyzer/ActiveSpeaker";
import { getActiveRoom } from "../Twilio/twilioCallUtility";
import { handleSendOfflineModeCommandToGuestUser } from "../../shared/dataTrackFunction";
import { LogFirebase } from "../../services/firestore";
import googleEvent from "../../services/googleEvent";
import { EVENT_NAME } from "../../constants/GTagConstants";

const LocalParticipantInfo = ({ localUserQualityValue, localisedValues }) => {
    const browserInfo = getExactBrowserName();
    const { batteryValue, isLocalDeviceBatteryLow, localNetworkTypeTitle } = useSelector((state) => state.batteryNetworkInfo);
    const { startLocalAudio, startLocalVideo, localUserNetworkQuality } = useSelector((state) => state.twilioEvents);
    const { audioPermission, cameraPermission } = useSelector((state) => state.callSetting);
    const { nqAvailable } = useSelector((state) => state.commonReducer);

    const room = getActiveRoom();

    const handleOfflineMode = (participantIdentity) => {
        googleEvent.logEvent(EVENT_NAME.OFFLINE_MODE_TAPPED);
        LogFirebase("OfflineMode", "Agent_start_own", "clicked_on_enable_offline_mode");
        handleSendOfflineModeCommandToGuestUser(participantIdentity);
    };

    return (
        <div className={clsx("accordion-item participant-box", !nqAvailable && "participant-no-network")} data-testid='participant-box'>
            <div className='participant-head' id='participant-heading-1'>
                <div className='participant-head-in'>
                    {nqAvailable && (
                        <div
                            className='participant-network-out'
                            data-bs-toggle='collapse'
                            data-bs-target='#participant-collapse-1'
                            aria-expanded='true'
                            aria-controls='participant-collapse-1'
                        >
                            <div className='participant-network custom-tooltip right-tooltip tooltip-auto-width m-hide'>
                                <div className='session-network'>
                                    {isNull(localUserNetworkQuality) && <span className='for-close-icon icon-close'></span>}
                                    <div className='for-base-icon'>
                                        <span className='for-icon icon-network'></span>
                                    </div>
                                    {localUserNetworkQuality >= NQ_UNSTABLE_PARAM && (
                                        <div className='for-over-icon'>
                                            <span
                                                className='for-icon icon-network'
                                                style={{ width: localUserQualityValue?.percentage || "0%" }}
                                            ></span>
                                        </div>
                                    )}
                                    {!isNull(localUserNetworkQuality) && localUserNetworkQuality < NQ_UNSTABLE_PARAM && (
                                        <div className='for-over-icon'>
                                            <span className='for-icon icon-network danger-text'></span>
                                        </div>
                                    )}
                                </div>
                                {!localUserQualityValue?.percentage && (
                                    <div className='custom-tooltip-content text-nowrap'>
                                        <div className='custom-tooltip-in'>
                                            <p>{localisedValues?.network_quality_not_available}</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                    <div
                        className='participant-head-left'
                        data-bs-toggle='collapse'
                        data-bs-target='#participant-collapse-1'
                        aria-expanded='true'
                        aria-controls='participant-collapse-1'
                    >
                        <div className='participant-info-name'>
                            <div className='participant-name-wrap custom-tooltip right-tooltip tooltip-auto-width m-hide'>
                                <div className='participant-name-wrap-text' data-testid='agent-participant-username'>
                                    {CALL_DATA.participantUserName}
                                </div>
                                <div className='custom-tooltip-content'>
                                    <div className='custom-tooltip-in'>
                                        <p>{CALL_DATA.participantUserName}</p>
                                    </div>
                                </div>
                            </div>
                            <div className='you-mark'>{localisedValues?.you}</div>
                        </div>
                    </div>
                    <div className='participant-head-right'>
                        {GLOBAL_MEETING_DATA.isAgent && GLOBAL_MEETING_DATA.device_type == DEVICE_TYPES.ANDROID_BROWSER && batteryValue && (
                            <div
                                className={clsx(
                                    "participant-action-info participant-battery-info custom-tooltip tooltip-auto-width m-hide",
                                    !isLocalDeviceBatteryLow && "battery-normal"
                                )}
                            >
                                <div className='battery-info-in'>
                                    <div className='battery-text'>
                                        {parseInt(batteryValue, 10)}
                                        {"%"}
                                    </div>
                                    <div className='participant-action-icon'>
                                        <span className='for-icon icon-low-battery'></span>
                                    </div>
                                </div>
                                <div className='custom-tooltip-content text-nowrap'>
                                    <div className='custom-tooltip-in'>
                                        <p>{localisedValues?.batteryinfo_tooltip}</p>
                                    </div>
                                </div>
                            </div>
                        )}
                        <div className='participant-action-info custom-tooltip tooltip-auto-width m-hide'>
                            <div className='participant-action-icon'>
                                {startLocalAudio && (
                                    <>
                                        <span className='for-icon icon-mic-on-stroke light-icon' data-testid='icon-audio-active'></span>
                                        <ActiveSpeaker userIdentity={CALL_DATA.localIdentity} isUserAudioEnabled={startLocalAudio} fromWhere='list' />
                                    </>
                                )}
                                {!startLocalAudio &&
                                    (audioPermission === MEDIA_PERMISSIONS.PROMPT || audioPermission === MEDIA_PERMISSIONS.GRANTED) && (
                                        <span className='for-icon light-icon icon-mute-stroke' data-testid='icon-audio-mute'></span>
                                    )}
                                {!startLocalAudio && audioPermission === MEDIA_PERMISSIONS.BLOCKED && (
                                    <span className='for-blocked-icon' data-testid='icon-audio-blocked'>
                                        <span className='for-icon light-icon icon-unmute'></span>
                                        <span className='for-close-icon'>
                                            <span className='for-close-icon-in icon-close-light'></span>
                                        </span>
                                    </span>
                                )}
                            </div>
                            <div className='custom-tooltip-content text-nowrap'>
                                <div className='custom-tooltip-in'>
                                    {startLocalAudio && <p>{localisedValues?.microphone_unmuted}</p>}
                                    {!startLocalAudio &&
                                        (audioPermission === MEDIA_PERMISSIONS.PROMPT || audioPermission === MEDIA_PERMISSIONS.GRANTED) && (
                                            <p>{localisedValues?.microphone_muted}</p>
                                        )}
                                    {!startLocalAudio && audioPermission === MEDIA_PERMISSIONS.BLOCKED && (
                                        <p>{localisedValues?.microphone_blocked}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className='participant-action-info custom-tooltip tooltip-auto-width m-hide'>
                            <div className='participant-action-icon'>
                                {startLocalVideo && <span className='for-icon icon-video active' data-testid='icon-video-active'></span>}
                                {!startLocalVideo &&
                                    (cameraPermission === MEDIA_PERMISSIONS.PROMPT || cameraPermission === MEDIA_PERMISSIONS.GRANTED) && (
                                        <span className='for-icon light-icon icon-video-off-stroke' data-testid='icon-video-off'></span>
                                    )}
                                {!startLocalVideo && cameraPermission === MEDIA_PERMISSIONS.BLOCKED && (
                                    <span className='for-blocked-icon' data-testid='icon-video-blocked'>
                                        <span className='for-icon light-icon icon-video'></span>
                                        <span className='for-close-icon'>
                                            <span className='for-close-icon-in icon-close-light'></span>
                                        </span>
                                    </span>
                                )}
                            </div>
                            <div className='custom-tooltip-content text-nowrap'>
                                <div className='custom-tooltip-in'>
                                    {startLocalVideo && <p>{localisedValues?.camera_on}</p>}
                                    {!startLocalVideo &&
                                        (cameraPermission === MEDIA_PERMISSIONS.PROMPT || cameraPermission === MEDIA_PERMISSIONS.GRANTED) && (
                                            <p>{localisedValues?.camera_off}</p>
                                        )}
                                    {!startLocalVideo && cameraPermission === MEDIA_PERMISSIONS.BLOCKED && <p>{localisedValues?.camera_blocked}</p>}
                                </div>
                            </div>
                        </div>
                        {isOfflinePhotoCaptureFeatureAvailable() && (
                            <div className='participants-control-dropdown own-participants-control'>
                                <div className='dropdown custom-dropdown'>
                                    <button
                                        className='btn dropdown-toggle triangle-none participants-control-btn'
                                        type='button'
                                        id='participants-control-dropdown-agent'
                                        data-bs-toggle='dropdown'
                                        aria-expanded='false'
                                    >
                                        <span className='for-icon icon-more'></span>
                                    </button>
                                    <div className='dropdown-menu right' aria-labelledby='participants-control-dropdown-agent'>
                                        <div className='dropdown-menu-in'>
                                            <ul>
                                                <li>
                                                    <div className='dropdown-item-div'>
                                                        <button
                                                            className='dropdown-item participants-control-action-btn'
                                                            onClick={() => handleOfflineMode(CALL_DATA.localIdentity)}
                                                            disabled={room?.participants?.size === 0}
                                                        >
                                                            <span className='for-icon icon-offline-mode'></span>
                                                            <span className='for-text'>{localisedValues?.enable_offline_mode}</span>
                                                        </button>
                                                    </div>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                        <div
                            className='accordion-header-arrow-out'
                            data-bs-toggle='collapse'
                            data-bs-target='#participant-collapse-1'
                            aria-expanded='true'
                            aria-controls='participant-collapse-1'
                        >
                            <div className='accordion-header-arrow'>
                                <span className='for-icon icon-up-arrow'></span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div id='participant-collapse-1' className='accordion-collapse collapse show' aria-labelledby='participant-heading-1'>
                <div className='accordion-body'>
                    <div className='participant-detail'>
                        {nqAvailable && localUserQualityValue?.name && (
                            <div className='participant-detail-row'>
                                <span className='for-lebel'>{localisedValues?.network_quality}: </span>
                                <span className='for-value'>{localUserQualityValue?.name}</span>
                            </div>
                        )}
                        <div className='participant-detail-row'>
                            {DetectRTC.osName}&nbsp;
                            {browserInfo.browserName}&nbsp;
                            {browserInfo.browserVersion}
                        </div>
                        {localNetworkTypeTitle && (
                            <div className='participant-detail-row'>
                                <span className='for-lebel'>{localisedValues?.network_type}: </span>
                                <span className='for-value'>{localNetworkTypeTitle}</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
LocalParticipantInfo.propTypes = {
    localUserQualityValue: PropTypes.any,
    localisedValues: PropTypes.any,
};
export default LocalParticipantInfo;
